import React, {useCallback} from 'react';
import PropTypes from 'prop-types';
import Carousel from 'react-multi-carousel';
import ProductCard from '../../components/Products/ProductCard/index';
import ProductSkeleton from '../Products/ProductSkeleton/ProductSkeleton';
import {
	ProductCardWrapper,
	Wrapper,
	Prev,
	Next,
} from '../../containers/Products/Products.style';
import {PromoCard} from '../../containers/PromoProducts/PromoProducts.style';
import {ArrowNext, ArrowPrev} from '../AllSvgIcon';
import {PromoSection} from '../../styled/pages.style';
import t from '../../i18n/translate';
import {useProductCardProps} from '../../hooks/useProductCardProps';

/**
 * Carousel navigation buttons
 */
const PrevButton = ({onClick}) => (
	<Prev onClick={(e) => {
		e.preventDefault();
		onClick();
	}} className="prevButton">
		<ArrowPrev/>
	</Prev>
);

const NextButton = ({onClick}) => (
	<Next onClick={(e) => {
		e.preventDefault();
		onClick();
	}} className="nextButton">
		<ArrowNext/>
	</Next>
);

const ButtonGroup = ({next, previous}) => (
	<Wrapper>
		<PrevButton onClick={() => previous()}/>
		<NextButton onClick={() => next()}/>
	</Wrapper>
);

/**
 * Carousel responsive configuration
 */
const CAROUSEL_RESPONSIVE = {
	desktop: {
		breakpoint: {max: 9999, min: 1200},
		items: 7,
		slidesToSlide: 7,
	},
	tablet: {
		breakpoint: {max: 1199, min: 768},
		items: 4,
		slidesToSlide: 4,
	},
	mobile: {
		breakpoint: {max: 767, min: 401},
		items: 2,
		slidesToSlide: 2,
	},
	smallScreen: {
		breakpoint: {max: 400, min: 0},
		items: 2,
		slidesToSlide: 2,
	},
};

/**
 * Skeleton loading carousel
 */
const SkeletonCarousel = ({deviceType, cartType}) => (
	<PromoSection>
		<Carousel
			arrows={false}
			responsive={CAROUSEL_RESPONSIVE}
			ssr
			showDots={false}
			infinite={false}
			containerClass="container-for-promo"
			deviceType={deviceType}
			renderButtonGroupOutside
			additionalTransfrom={0}
			customButtonGroup={<ButtonGroup/>}
		>
			{[...Array(10)].map((_, index) => (
				<ProductCardWrapper style={{padding: '20px'}} key={`skeleton-${index}`}>
					<ProductSkeleton cartType={cartType}/>
				</ProductCardWrapper>
			))}
		</Carousel>
	</PromoSection>
);

/**
 * ProductCarousel Component
 * Reusable carousel for displaying products (promotions, new arrivals, etc.)
 */
const ProductCarousel = ({
	                         products = [],
	                         loading = false,
	                         deviceType = 'desktop',
	                         cartType = 'normal',
	                         HeaderComponent,
	                         emptyMessage,
                         }) => {
	// Use custom hook for product card props mapping
	const {mapProductToCardProps} = useProductCardProps();

	// Render product card
	const renderProductCard = useCallback((item, index) => {
		return (
			<ProductCardWrapper style={{padding: '20px'}} key={`product-${item.id}-${index}`}>
				<ProductCard {...mapProductToCardProps(item)} />
			</ProductCardWrapper>
		);
	}, [mapProductToCardProps]);

	// Render loading state
	if (loading) {
		return (
			<>
				{HeaderComponent}
				<SkeletonCarousel deviceType={deviceType} cartType={cartType}/>
			</>
		);
	}

	// Render empty state
	if (!products || products.length === 0) {
		return (
			<>
				{HeaderComponent}
				<PromoCard className="card-height">
					<span>{emptyMessage || t('common_no_products_found_label')}</span>
				</PromoCard>
			</>
		);
	}

	// Render products carousel
	return (
		<>
			{HeaderComponent}
			<PromoSection>
				<Carousel
					arrows={false}
					responsive={CAROUSEL_RESPONSIVE}
					ssr
					showDots={false}
					infinite
					containerClass="container-for-promo"
					deviceType={deviceType}
					autoPlay
					autoPlaySpeed={4000}
					renderButtonGroupOutside
					additionalTransfrom={0}
					customButtonGroup={<ButtonGroup/>}
				>
					{products.map(renderProductCard)}
				</Carousel>
			</PromoSection>
		</>
	);
};

ProductCarousel.propTypes = {
	products: PropTypes.array,
	loading: PropTypes.bool,
	deviceType: PropTypes.string,
	cartType: PropTypes.string,
	HeaderComponent: PropTypes.node,
	emptyMessage: PropTypes.string,
};

export default React.memo(ProductCarousel);
